import axiosInstance from "./axiosService";

interface SignedURLResponse {
  status?: string;
  message?: string;
  signed_url?: string;
  error?: string;
}

export interface Base64FileResponse {
  status: string;
  message?: string;
  file_content_b64?: string;
  gcs_path?: string;
}
interface BulkSignedURLResponse {
  status: string;
  message: string;
  data: Record<string, string | null>;
}

interface MessageResponse {
  status: "error" | "warning" | "info" | "success";
  message: string;
  auto_login?: boolean;
}

export const changePassword = async (
  data: Record<string, any>
): Promise<void> => {
  const response = await axiosInstance.post(`account/change-password/`, data);
  return response.data;
};

export const signedURL = async (
  data: Record<string, any>
): Promise<SignedURLResponse> => {
  try {
    const response = await axiosInstance.post(
      "account/gcp-presigned-url/",
      data
    );
    return response.data; // Ensure this is properly returned
  } catch (error: any) {
    console.error("API error:", error);
    return {
      status: "error",
      message: "Failed to fetch the document. Please try again later",
    };
  }
};

export const getBase64File = async (
  data: Record<string, any>
): Promise<Base64FileResponse> => {
  try {
    const response = await axiosInstance.post(
      "account/gcp-signature-base/",
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("API error:", error);
    return {
      status: "error",
      message: "Failed to fetch the base64 file. Please try again later.",
    };
  }
};

/**
 * This function sends a POST request to the backend API (`/bulk-signed-url/`)
 * with a list of file names and retrieves signed URLs for each file.
 * @param fileNames - An array of file names for which signed URLs are requested.
 * @returns A Promise that resolves to an object containing the status of the request
 *          ("success" or "error"), a message, and a `data` object with file names
 *          as keys and their corresponding signed URLs (or null if unavailable) as values.
 */
export const bulkSignedURL = async (
  fileNames: string[]
): Promise<BulkSignedURLResponse> => {
  try {
    // Send the list of file names to the backend
    const response = await axiosInstance.post("account/bulk-signed-url/", {
      file_names: fileNames,
    });
    return response.data;
  } catch (error: any) {
    console.error("API error:", error);
    return {
      status: "error",
      message: "Failed to fetch the documents. Please try again later.",
      data: {}, // Return an empty object if there's an error
    };
  }
};
export const signUpUser = async (
  data: Record<string, any>
): Promise<MessageResponse> => {
  const response = await axiosInstance.post(`account/signup/`, data);
  return response.data;
};

export const submitFeedback = async (
  data: Record<string, any>
): Promise<void> => {
  const response = await axiosInstance.post(`account/feedback/`, data);
  return response.data;
};

export const requestPasswordReset = async (input: string): Promise<void> => {
  const isEmail = input.includes("@") && input.includes(".");
  const payload = isEmail ? { email: input } : { phone_number: input.replace(/\D/g, "") };

  const response = await axiosInstance.post(`account/forgot-password/`, payload);
  return response.data;
};

export const resetPassword = async (
  token: string,
  password: string,
  confirm_password: string
) => {
  const response = await axiosInstance.post(`account/reset-password/`, {
    token,
    password,
    confirm_password,
  });
  return response.data;
};

export const verifyPassword = async (password: string) => {
  try {
    const response = await axiosInstance.post(`account/verify-password/`, {
      password,
    });
    return response.data;
  } catch (error: any) {
    console.error("API error:", error);
    return { status: "error", message: "Failed to verify password" };
  }
};

export const updateUserData = async (
  data: Record<string, any>,
  userId?: string
) => {
  const response = await axiosInstance.patch(`account/update-user/`, {
    data,
  });
  return response.data;
};

export const getUpdatedUserData = async (userId?: string) => {
  const response = await axiosInstance.get(`account/update-user/`, {});
  return response.data;
};

//'add-roles/

export const updateUserRole = async (
  action: "add" | "remove",
  role?: string
) => {
  let url = "account/add-roles/";
  const response = await axiosInstance.post(url, {
    action,
    role,
  });

  return response.data;
};

export const getUserData = async (userId: string, request_type: string) => {
  const response = await axiosInstance.get(`account/get-user-detail`, {
    params: { [request_type]: userId },
  });
  return response.data;
};

export const fetchUserPermissions = async (pkid: number) => {
  const response = await axiosInstance.get(`account/permissions/${pkid}/`);
  return response.data; // axios puts the response body in .data
};

export const updateUserPermissions = async (
  pkid: number,
  data: { add: string[]; remove: string[] }
) => {
  const response = await axiosInstance.patch(
    `account/permissions/${pkid}/`,
    data,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return response.data;
};

export const fetchUserReleaseNotifications = async () => {
  const response = await axiosInstance.get("account/release-notifications/");
  return response.data;
};

export const markReleaseNotificationRead = async (id?: number) => {
  const url = id
    ? `account/release-notifications/${id}/mark-read/`
    : `account/release-notifications/`;
  const response = await axiosInstance.patch(url);
  return response.data;
};

export const ssoLogin = async (token: string) => {
  const response = await axiosInstance.post("account/sso-login/", { token });
  return response.data;
};
